
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import AddCollectionForm from "@/components/forms/AddCollectionForm";
import { useQueryClient } from "@tanstack/react-query";
import CustomAddIcon from "@/components/icons/CustomAddIcon";

interface AddCollectionButtonProps {
  className?: string;
  variant?: "default" | "outline" | "secondary" | "icon" | "circle";
  id?: string;
  showHoverText?: boolean;
}

const AddCollectionButton = ({ 
  className, 
  variant = "default", 
  id,
  showHoverText = false
}: AddCollectionButtonProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const queryClient = useQueryClient();

  const handleMouseEnter = () => {
    if (showHoverText) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (showHoverText) {
      setIsHovered(false);
    }
  };

  const handleFormComplete = () => {
    // Invalidate collections query to trigger a refresh
    queryClient.invalidateQueries({ queryKey: ['collections'] });
    setIsDialogOpen(false);
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        {variant === "circle" ? (
          <div 
            className={`flex items-center ${className}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {isHovered && (
              <span className="text-[#0073FF] text-sm font-medium mr-2">Add</span>
            )}
            <Button 
              variant="ghost" 
              size="icon" 
              className="p-0 h-7 w-7 flex items-center justify-center hover:bg-transparent"
              id={id}
            >
              <CustomAddIcon 
                size={24} 
                variant={isHovered ? "blue" : "gray"} 
              />
            </Button>
          </div>
        ) : variant === "icon" ? (
          <Button 
            variant="ghost" 
            size="icon" 
            className={className}
            id={id}
          >
            <CustomAddIcon size={24} variant="blue" />
          </Button>
        ) : (
          <Button 
            className={`bg-[#0073FF] hover:bg-[#0060D6] transition-colors flex items-center gap-0 px-3 py-2 ${className}`}
            id={id}
          >
            <span className="mr-0">Add Collection</span>
            <CustomAddIcon size={24} variant="blue" className="-ml-[2px]" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Create New Collection</DialogTitle>
          <DialogDescription>
            Create a new collection to organize your content
          </DialogDescription>
        </DialogHeader>
        <AddCollectionForm onComplete={handleFormComplete} />
      </DialogContent>
    </Dialog>
  );
};

export default AddCollectionButton;
